import React, { useEffect, useState } from "react";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";
import { useService } from "../../../context/ServiceContext";
import { Copy, FileJson, Info, ListCheck, Loader } from "lucide-react";
import { verifyRcDetailsFull } from "../../../Services/ApiTestService/apiTestService";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { isNumeric, isAlpha, isAlphanumeric } from "validator";

function RcVerificationFull() {
  const { serviceName } = useService();

  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);

  const [rc, setRc] = useState(null);

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const isAlphaNumericdl = (value) => {
    if (!value) return;

    return isAlphanumeric(String(value).trim(), "en-IN", { ignore: " " });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(setIsJsonCopied(true));
    showSuccessToast("Text Copied");
  };
  // ---------------- VERIFY CONSUMER ----------------
  const handleVerifyConsumer = async () => {
    if (!profileData) return;

    const validatePan = isAlphaNumericdl(rc);

    if (!validatePan) {
      return setInputError(
        "⚠️ Please enter AlphaNumeric values in Pan No. field",
      );
    } else if (rc.length < 10) {
      return setInputError("⚠️ Enter at least 10 digits in Pan No. field");
    }

    try {
      setIsLoading(true);
      setResponse(null);
      setInputError(null);

      const res = await verifyRcDetailsFull({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        rc: rc.trim(),
      });

      if (res) {
        apiResponse = res;
        setResponse(apiResponse);
   
      }

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "request successful");
      } else if (res?.status?.code === 422) {
        showErrorToast(response?.message || "Invalid format");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!response.success) {
        showErrorToast(response?.data?.message || "verificaton failed");
      } else if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else {
        showErrorToast(res?.status?.message || "Verification failed");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading={serviceName} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center py-14">
        {/* ---------------- FORM ---------------- */}
        <div className=" max-w-3xl border rounded-md p-4 sm:p-6 mt-6 bg-white">
          <div className="flex flex-wrap items-center gap-4">
            {/* Mobile */}
            <div className="flex items-center gap-4">
              <label className=" font-bold">
                Rc No: <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={rc}
                maxLength={10}
                onChange={(e) => setRc(e.target.value.toUpperCase())}
                className="flex-1 border border-gray-400 uppercase rounded-md px-3 h-[38px]"
                placeholder="Eg: HYT363GHT8"
              />
            </div>

            {/* input errors */}

            {inputError && (
              <div className="text-red-700 text-xl font-bold">{inputError}</div>
            )}

            {/* Verify Button */}
            <div className="flex">
              <button
                disabled={isLoading || !rc}
                onClick={handleVerifyConsumer}
                className="px-6 py-2 bg-gradient-to-br active:from-green-600 active:to-green-700 from-blue-400 to-blue-800 text-white rounded-sm disabled:opacity-50"
              >
                Verify
              </button>
            </div>
          </div>
        </div>

        {response && (
          <div
            className={` ${response.data?.status?.code === 500 || response.data?.status?.code === 422 ? "text-red-500" : "text-green-600"} py-4 text-2xl font-bold`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* instructions */}

        <div>
          <div className="bg-green-50 text-green-700 border border-green-400 p-5 mt-12 rounded-md">
            <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
              <Info /> Important Instructions !!!
            </h3>

            <p className="text-sm leading-relaxed">
              Please enter a valid RC No. clicking the Verify button. After
              successful verification, the system will return the response in
              structured table and JSON format, including contact details
              associated with the provided details. You may review and copy the
              data for your reference or further use.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl max-h-[600px] overflow-y-auto">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8"
              >
                {isJsonCopied ? "copied" : <Copy />}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-8" />{" "}
              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            {/* -------- USER DETAILS -------- */}
            <div className="bg-blue-600 text-white  p-5 rounded-md">
              <h2 className="text-2xl flex items-center justify-center gap-2  font-bold mb-1">
                <ListCheck /> All Details
              </h2>
              <hr className="mb-8 w-full border-t border-gray-200" />

              {/* BASIC INFO */}
             {/* FULL RC DETAILS */}
<div className="space-y-2 text-sm bg-blue-100 px-4 lg:px-6 py-4 rounded-lg">

  {/* Basic RC Info */}
  <Detail label="RC Number" value={response?.data?.rc_number} />
  <Detail label="RC Status" value={response?.data?.rc_status} />
  <Detail label="Owner Name" value={response?.data?.owner_name} />
  <Detail label="Father Name" value={response?.data?.father_name} />
  <Detail label="Owner Number" value={response?.data?.owner_number} />
  <Detail label="Mobile Number" value={response?.data?.mobile_number} />

  {/* Address */}
  <Detail label="Present Address" value={response?.data?.present_address} />
  <Detail label="Permanent Address" value={response?.data?.permanent_address} />
  <Detail label="Registered At" value={response?.data?.registered_at} />
  <Detail label="RTO Code" value={response?.data?.rto_code} />

  {/* Vehicle Info */}
  <Detail label="Vehicle Category" value={response?.data?.vehicle_category} />
  <Detail label="Vehicle Category Description" value={response?.data?.vehicle_category_description} />
  <Detail label="Maker Description" value={response?.data?.maker_description} />
  <Detail label="Maker Model" value={response?.data?.maker_model} />
  <Detail label="Variant" value={response?.data?.variant} />
  <Detail label="Body Type" value={response?.data?.body_type} />
  <Detail label="Fuel Type" value={response?.data?.fuel_type} />
  <Detail label="Color" value={response?.data?.color} />
  <Detail label="Cubic Capacity" value={response?.data?.cubic_capacity} />
  <Detail label="No. of Cylinders" value={response?.data?.no_cylinders} />
  <Detail label="Seat Capacity" value={response?.data?.seat_capacity} />
  <Detail label="Sleeper Capacity" value={response?.data?.sleeper_capacity} />
  <Detail label="Standing Capacity" value={response?.data?.standing_capacity} />
  <Detail label="Wheelbase" value={response?.data?.wheelbase} />
  <Detail label="Vehicle Gross Weight" value={response?.data?.vehicle_gross_weight} />
  <Detail label="Unladen Weight" value={response?.data?.unladen_weight} />

  {/* Engine & Chassis */}
  <Detail label="Engine Number" value={response?.data?.vehicle_engine_number} />
  <Detail label="Chassis Number" value={response?.data?.vehicle_chasi_number} />

  {/* Dates */}
  <Detail label="Registration Date" value={response?.data?.registration_date} />
  <Detail label="Manufacturing Date" value={response?.data?.manufacturing_date} />
  <Detail label="Manufacturing Date (Formatted)" value={response?.data?.manufacturing_date_formatted} />
  <Detail label="Fit Upto" value={response?.data?.fit_up_to} />
  <Detail label="Tax Upto" value={response?.data?.tax_upto} />
  <Detail label="Tax Paid Upto" value={response?.data?.tax_paid_upto} />
  <Detail label="PUCC Number" value={response?.data?.pucc_number} />
  <Detail label="PUCC Upto" value={response?.data?.pucc_upto} />
  <Detail label="Latest By" value={response?.data?.latest_by} />

  {/* Insurance */}
  <Detail label="Insurance Company" value={response?.data?.insurance_company} />
  <Detail label="Insurance Policy Number" value={response?.data?.insurance_policy_number} />
  <Detail label="Insurance Upto" value={response?.data?.insurance_upto} />

  {/* Finance */}
  <Detail label="Financer" value={response?.data?.financer} />
  <Detail label="Financed" value={response?.data?.financed ? "Yes" : "No"} />

  {/* Permit */}
  <Detail label="Permit Number" value={response?.data?.permit_number} />
  <Detail label="Permit Issue Date" value={response?.data?.permit_issue_date} />
  <Detail label="Permit Valid From" value={response?.data?.permit_valid_from} />
  <Detail label="Permit Valid Upto" value={response?.data?.permit_valid_upto} />
  <Detail label="Permit Type" value={response?.data?.permit_type} />
  <Detail label="National Permit Number" value={response?.data?.national_permit_number} />
  <Detail label="National Permit Upto" value={response?.data?.national_permit_upto} />
  <Detail label="National Permit Issued By" value={response?.data?.national_permit_issued_by} />

  {/* Other Info */}
  <Detail label="Blacklist Status" value={response?.data?.blacklist_status} />
  <Detail label="NOC Details" value={response?.data?.noc_details} />
  <Detail label="Norms Type" value={response?.data?.norms_type} />
  <Detail label="Non Use Status" value={response?.data?.non_use_status} />
  <Detail label="Non Use From" value={response?.data?.non_use_from} />
  <Detail label="Non Use To" value={response?.data?.non_use_to} />
  <Detail label="Less Info" value={response?.data?.less_info ? "Yes" : "No"} />
  <Detail label="Masked Name" value={response?.data?.masked_name ? "Yes" : "No"} />

</div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default RcVerificationFull;

const Detail = ({ label, value }) => (
  <div className="flex">
    <span className="font-semibold text-[16px] text-blue-500 w-64">
      {label}:
    </span>
    <span className="text-black break-words">
      {value === null || value === "" ? "N/A" : value}
    </span>
  </div>
);